import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useLocation } from "react-router-dom";
import { getDisplayName } from "@/lib/utils";

/**
 * Global hook that listens for incoming messages and shows toast notifications
 * when the user is NOT on the /messagerie page.
 */
export const useMessageNotifications = () => {
  const { user } = useAuth();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Keep location ref in sync
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;

    // Pre-create audio element from public asset
    try {
      audioRef.current = new Audio("/notification.wav");
      audioRef.current.volume = 0.3;
    } catch {
      // Audio not supported
    }

    const channel = supabase
      .channel("global-message-notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        async (payload) => {
          const msg = payload.new as any;

          // Ignore own messages
          if (msg.sender_id === user.id) return;
          // Ignore blocked/deleted
          if (msg.blocked_reason || msg.is_deleted) return;

          // Check if user is participant in this conversation
          const { data: convo } = await supabase
            .from("conversations")
            .select("client_id, artisan_id, annonce_id")
            .eq("id", msg.conversation_id)
            .maybeSingle();

          if (!convo) return;
          if (convo.client_id !== user.id && convo.artisan_id !== user.id) return;

          // Skip if already on messagerie page
          if (locationRef.current === "/messagerie") return;

          // Play notification sound
          try {
            audioRef.current?.play();
          } catch {
            // Autoplay blocked
          }

          // Get sender name
          const { data: profile } = await supabase
            .from("profiles")
            .select("display_name, first_name, last_name")
            .eq("user_id", msg.sender_id)
            .maybeSingle();

          const senderName = getDisplayName(profile, "Nouveau message");

          const preview =
            msg.message_type === "image"
              ? "📷 Photo"
              : (msg.content || "").slice(0, 80);

          toast({
            title: senderName,
            description: preview,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);
};
